<template>
  <modal-component
    :is-displayed="isSourcesViewOpened"
    :remove-outside-click="true"
    :title="`Sources for Node ${nodeDetails.index}`"
    custom-modal-size="w-7/12"
    @toggle-modal="$emit('toggle-modal')"
  >
    <create-form
      v-if="isCreateModalOpened"
      :is-create-modal-opened="isCreateModalOpened"
      :node-id="nodeDetails.id"
      :refresh-data="async () => getNodeSources(nodeDetails.id)"
      @toggle-modal="isCreateModalOpened = false"
    />
    <edit-form
      v-if="isEditModalOpened"
      :is-edit-modal-opened="isEditModalOpened"
      :refresh-data="async () => getNodeSources(nodeDetails.id)"
      :source-prop="selectedSource"
      @toggle-modal="isEditModalOpened = false"
    />
    <delete-modal
      :is-deleting="isDeleteModalOpened"
      @cancel-delete="isDeleteModalOpened = false"
      @toggle-modal="isDeleteModalOpened = false"
      @confirm-delete="openConfirmationModal('delete')"
    />
    <confirmation-modal
      :delete-text="confirmationModalText"
      :is-confirmation-modal-opened="isConfirmationModalOpened"
      @confirm-action="confirmationModalAction"
      @close-confirmation-modal="isConfirmationModalOpened = false"
      @toggle-modal="isConfirmationModalOpened = false"
    />
    <div v-if="isLoading" class="flex w-full items-center justify-center">
      <small-loader />
    </div>
    <div v-else class="px-6">
      <table-action-header
        :icon="['fas', 'add']"
        button-text="Create"
        @execute-method="toggleCreateModal"
      />
      <custom-table
        :is-data-source-loading="isDatasourceLoading"
        :options="options"
        :table-data="sources"
        :table-headers="tableHeaders"
        @selected-rows="getSelectedRows"
      />
    </div>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import CustomTable from "@/core/Components/CustomTable.vue";
import { Source } from "@/modules/Parkom/Submodules/Displays/Services/DisplayTypes";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import DisplayService from "@/modules/Parkom/Submodules/Displays/Services/DisplayService";
import { useToast } from "vue-toastification";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import Loader from "@/core/Components/Loader.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import CreateForm from "./CreateForm.vue";
import EditForm from "./EditForm.vue";
import DeleteModal from "@/core/Components/Notifications/DeleteModal.vue";
import ConfirmationModal from "@/core/Components/Notifications/ConfirmationModal.vue";
import { TableDropdownItem } from "@/core/Services/GlobalTypes";

export default defineComponent({
  components: {
    ConfirmationModal,
    DeleteModal,
    SmallLoader,
    Loader,
    TableActionHeader,
    CustomTable,
    ModalComponent,
    CreateForm,
    EditForm,
  },
  props: {
    isSourcesViewOpened: Boolean,
    displayId: {
      type: Number,
      required: true,
    },
    nodeDetails: {
      type: Object as PropType<{ id: number; index: number }>,
      required: true,
    },
  },
  data() {
    return {
      displayService: new DisplayService(),
      toast: useToast(),
      sources: [] as Source[],
      selectedSource: {} as Source,
      isLoading: false,
      isCreateModalOpened: false,
      isDatasourceLoading: false,
      isEditModalOpened: false,
      isDeleteModalOpened: false,
      isConfirmationModalOpened: false,
      confirmationModalText: "",
      tableHeaders: {
        id: { header: "ID", visible: false },
        showFree: { header: "Show Free", visible: false },
        displayNodeId: { header: "Display Node ID", visible: true },
        "space.name": { header: "Space", visible: true },
        "spot.name": { header: "Spot", visible: true },
        "spotType.name": { header: "Spot type", visible: true },
      },
      options: {
        edit: {
          header: "Edit",
          icon: ["fas", "pen"],
          action: this.toggleEditModal,
        },
        delete: {
          header: "Delete",
          icon: ["fas", "trash"],
          action: this.toggleDeleteModal,
        },
      } as TableDropdownItem,
    };
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getNodeSources(this.nodeDetails.id);
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async getNodeSources(nodeId: number) {
      this.isDatasourceLoading = true;
      try {
        this.sources = await this.displayService.getNodeSources(nodeId);
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isDatasourceLoading = false;
      }
    },
    async deleteSource() {
      try {
        await this.displayService.deleteSource(this.selectedSource.id);
        this.toast.success((this as any).$t("Displays.SourceDeletedSuccessfully"));
        this.isDeleteModalOpened = false;
        await this.getNodeSources(this.selectedSource.id);
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    async confirmationModalAction() {
      switch (this.confirmationModalText) {
        case "delete":
          await this.deleteSource();
          break;
      }
      this.isConfirmationModalOpened = false;
    },

    getSelectedRows(values: Source[]) {
      if (values.length) {
        this.selectedSource = values[0];
      }
    },
    openConfirmationModal(key: string) {
      this.isConfirmationModalOpened = true;
      this.confirmationModalText = key;
    },
    toggleCreateModal() {
      this.isCreateModalOpened = !this.isCreateModalOpened;
    },
    toggleEditModal() {
      this.isEditModalOpened = !this.isEditModalOpened;
    },
    toggleDeleteModal() {
      this.isDeleteModalOpened = !this.isDeleteModalOpened;
    },
  },
});
</script>

<style scoped></style>
